import type { Ingredient, MenuItem, Recipe } from './types';
import { buildContext, computeIngredientCost, computeRecipeCostPartial } from './costing';

export interface CatalogCompleteness {
  dishesTotal: number;
  dishesCosted: number;
  dishesPartial: number;
  ingredientsUsed: number;
  ingredientsPriced: number;
  pct: number;
  nextActions: Array<{ kind: 'add-recipe' | 'price-ingredient' | 'scan-invoice'; label: string; count: number }>;
}

/** Costear una subreceta no basta: se comprueban todas sus dependencias. */
export function catalogCompleteness(recipes: Recipe[], ingredients: Ingredient[], menuItems: MenuItem[]): CatalogCompleteness {
  const context = buildContext(ingredients, recipes);
  const dishes = menuItems.filter((item) => item.active).length
    ? menuItems.filter((item) => item.active).map((item) => item.recipeId)
    : recipes.filter((recipe) => recipe.active && recipe.kind === 'plato').map((recipe) => recipe.recipeId);
  const used = new Set<string>();
  const visited = new Set<string>();
  const visit = (recipeId: string) => {
    if (visited.has(recipeId)) return;
    visited.add(recipeId);
    for (const component of context.recipes.get(recipeId)?.components ?? []) {
      if (component.ref.kind === 'ingredient') used.add(component.ref.ingredientId);
      else visit(component.ref.recipeId);
    }
  };
  dishes.forEach(visit);
  let dishesCosted = 0;
  let dishesPartial = 0;
  let noRecipe = 0;
  for (const id of dishes) {
    const recipe = context.recipes.get(id);
    if (!recipe?.components.length) { noRecipe += 1; continue; }
    try {
      const cost = computeRecipeCostPartial(id, context);
      if (cost.status === 'complete') dishesCosted += 1;
      else dishesPartial += 1;
    } catch { dishesPartial += 1; }
  }
  let ingredientsPriced = 0;
  for (const id of used) {
    const ingredient = context.ingredients.get(id);
    if (!ingredient || ingredient.pricePending) continue;
    try { computeIngredientCost(ingredient); ingredientsPriced += 1; } catch { /* pendiente */ }
  }
  const missingPrices = used.size - ingredientsPriced;
  const nextActions: CatalogCompleteness['nextActions'] = [];
  if (missingPrices) {
    nextActions.push({ kind: 'price-ingredient', count: missingPrices, label: `Confirma ${missingPrices} precios para afinar el coste de tus platos` });
    nextActions.push({ kind: 'scan-invoice', count: missingPrices, label: 'Sube una factura para completar los precios' });
  }
  if (noRecipe || !dishes.length) nextActions.push({ kind: 'add-recipe', count: noRecipe || 1, label: dishes.length ? `Cuéntale a Alexia los ${noRecipe} platos sin escandallo` : 'Cuéntale a Alexia tu plato estrella' });
  else if (dishesPartial && !missingPrices) nextActions.push({ kind: 'add-recipe', count: dishesPartial, label: `Revisa cantidades y unidades de ${dishesPartial} escandallos` });
  return { dishesTotal: dishes.length, dishesCosted, dishesPartial, ingredientsUsed: used.size, ingredientsPriced,
    pct: dishes.length ? Math.round(dishesCosted / dishes.length * 100) : 0, nextActions };
}
